import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, Polyline, Pane, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { MapPin, RefreshCw, Flame, Layers, Clock, Building2, GripVertical, ChevronDown, ChevronUp, Crosshair, Info, Sun, Moon, Check, UserCheck, X, AlertTriangle, Zap } from "lucide-react";
import { fetchScanMap, type ScanMapPoint } from "../../services/scanMap";
import { fetchHospitals, type Hospital } from "../../services/hospitals";
import { buildHospitalColorMap } from "../../utils/hospitalColors";
import { fetchOfficers, type Officer } from "../../services/officers";
import { cn } from "../../lib/utils";
import { useStickyHospital } from "../../hooks/useStickyHospital";
import { useTranslation } from "../../i18n";
import { MAP_STYLES, DEFAULT_STYLE_INDEX, MAP_STYLE_STORAGE_KEY } from "../../components/map/mapStyles";

/** Officer mode gradient: oldest scan blue (hue 217) -> newest red (hue 0). */
function timeGradientColor(ts: number, min: number, max: number): string {
  const fraction = Math.min(1, Math.max(0, (ts - min) / Math.max(max - min, 1)));
  return `hsl(${Math.round(217 * (1 - fraction))}, 82%, 52%)`;
}

// Center of metro Atlanta — where all Emory sites live. Used until scans load.
const ATLANTA_CENTER: [number, number] = [33.79, -84.38];

// Map tile styles, the default style index, and the storage key now live in the
// shared components/map/mapStyles module so the officer scan-history map offers
// the exact same basemap catalogue and picker as this page.

/**
 * useState that persists to localStorage, so a map view setting the user chose
 * (light/dark, heatmap vs markers, snap-to-building, auto-refresh, …) stays put
 * across visits and reloads instead of resetting to its default. `encode`/
 * `decode` serialize the value; storage failures fall back to in-memory state.
 */
function useStickyState<T>(
  key: string,
  initial: T,
  encode: (v: T) => string,
  decode: (s: string) => T
) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw == null ? initial : decode(raw);
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, encode(value));
    } catch {
      /* storage unavailable — keep working in-memory */
    }
  }, [key, value]);
  return [value, setValue] as const;
}

// Boolean (de)serializers for the sticky map toggles.
const boolTo = (v: boolean) => (v ? "true" : "false");
const boolFrom = (s: string) => s === "true";

// Hospital colors come from the shared canonical map (utils/hospitalColors) so a
// hospital reads with the SAME color here, on the comparison chart, and on the
// calendar — see buildHospitalColorMap below, fed the full hospital list.

// Known hospital abbreviations, keyed by a normalized (lowercased, trimmed) name.
// Falls back to an initials-based abbreviation for anything not listed here.
const HOSPITAL_ABBREVIATIONS: Record<string, string> = {
  "emory musculoskeletal institute": "EMSK",
  northlake: "NL",
  "emory johns creek hospital": "EJCH",
  "executive park 12": "EP12",
  "emory university hospital": "EUH",
  "emory university hospital (euh)": "EUH",
  "euh clifton tower": "EUHCT",
  "the emory clinics: a, b & c": "EC",
  "emory saint joseph hospital": "ESJH",
  "emory hillandale hospital": "EHH",
};

// Words that don't contribute to an initials-based abbreviation.
const ABBR_STOP_WORDS = new Set(["the", "of", "and", "&", "a", "an"]);

// Known hospital building centers, keyed by hospital_id. Derived from the average
// GPS position of all recorded scans at each site (see scan_events). Used by the
// "Snap to building" display fix to pull indoor GPS drift back toward the building.
const HOSPITAL_CENTERS: Record<string, [number, number]> = {
  "c3e35c34-eed1-40ff-a34f-7701f6c07fa3": [33.82843623, -84.33483732], // Emory Musculoskeletal Institute
  "61899120-4a8b-497d-862d-94436577382d": [33.83394786, -84.23074006], // Northlake
  "85629923-42f4-4ee9-889d-56a55dc041f6": [34.06672631, -84.17660777], // Emory Johns Creek Hospital
  "d4e35c34-eed1-40ff-a34f-7701f6c07fa3": [33.79420537, -84.27944156], // Executive Park 12
  "b1e35c34-eed1-40ff-a34f-7701f6c07fa3": [33.67155397, -84.10960765], // Emory University Hospital (EUH)
  "b2e35c34-eed1-40ff-a34f-7701f6c07fa3": [33.79229569, -84.32005190], // The Emory Clinics: A, B & C
  "e5e35c34-eed1-40ff-a34f-7701f6c07fa3": [33.79114926, -84.32042330], // Emory University Hospital Clifton Tower
  "2a664de3-f450-4415-bb2f-9ae230b7f117": [33.90850845, -84.34952764], // Emory Saint Joseph Hospital
  "ffa8dbb7-d0c1-402d-af01-a313f1495bc4": [33.73714753, -84.49153397], // Emory Hillandale Hospital
};

// "Snap to building" tuning. Indoor GPS can drift 10-50m; scans within SNAP_RADIUS_M
// of their hospital center are pulled toward it, preserving the relative scatter
// pattern but compressing the spread to ~SNAP_FACTOR of its original size.
const SNAP_RADIUS_M = 100;
const SNAP_FACTOR = 0.35; // keep 35% of the offset -> ~65% tighter cluster

/**
 * Applies the "snap to building" display adjustment to a scan's coordinates.
 * If the scan is within SNAP_RADIUS_M of its hospital center, its offset from the
 * center is scaled down by SNAP_FACTOR; otherwise the coordinates are returned as-is.
 * Pure display transform — the underlying GPS data is never modified.
 */
function snapToBuildingCenter(
  lat: number,
  lng: number,
  hospitalId: string | null | undefined
): [number, number] {
  const center = hospitalId ? HOSPITAL_CENTERS[hospitalId] : undefined;
  if (!center) return [lat, lng];
  const [cLat, cLng] = center;
  // Equirectangular approximation — accurate enough at the ~100m scale we care about.
  const metersPerDegLat = 111_320;
  const metersPerDegLng = 111_320 * Math.cos((cLat * Math.PI) / 180);
  const dLatM = (lat - cLat) * metersPerDegLat;
  const dLngM = (lng - cLng) * metersPerDegLng;
  const distM = Math.hypot(dLatM, dLngM);
  if (distM > SNAP_RADIUS_M) return [lat, lng];
  return [cLat + (lat - cLat) * SNAP_FACTOR, cLng + (lng - cLng) * SNAP_FACTOR];
}

/** Builds a short abbreviation from a hospital name (e.g. "Emory Johns Creek Hospital" -> "EJCH"). */
function hospitalAbbreviation(name: string): string {
  const normalized = name.trim().toLowerCase();
  if (HOSPITAL_ABBREVIATIONS[normalized]) return HOSPITAL_ABBREVIATIONS[normalized];

  const initials = name
    .replace(/\(.*?\)/g, " ") // drop parenthetical groups like "(EUH)"
    .split(/[\s:,]+/)
    .filter((w) => w && !ABBR_STOP_WORDS.has(w.toLowerCase()))
    .map((w) => w[0].toUpperCase())
    .join("");
  return initials || name;
}

/** Formats the "ABBR — Full Name" portion of a legend row (count is rendered separately). */
function legendLabel(name: string): string {
  const abbr = hospitalAbbreviation(name);
  return abbr && abbr !== name ? `${abbr} — ${name}` : name;
}

export type TimeRangeKey = "1h" | "24h" | "7d" | "30d" | "custom";

export const TIME_RANGES: { key: TimeRangeKey; label: string }[] = [
  { key: "1h", label: "Last hour" },
  { key: "24h", label: "Last 24h" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "custom", label: "Custom range" },
];

const REFRESH_INTERVAL_MS = 30_000;

export function startDateForRange(range: TimeRangeKey): Date | null {
  const now = Date.now();
  switch (range) {
    case "1h":
      return new Date(now - 60 * 60 * 1000);
    case "24h":
      return new Date(now - 24 * 60 * 60 * 1000);
    case "7d":
      return new Date(now - 7 * 24 * 60 * 60 * 1000);
    case "30d":
      return new Date(now - 30 * 24 * 60 * 60 * 1000);
    case "custom":
      return null;
  }
}

/**
 * Fits the map to the loaded scan points whenever the set of points changes.
 * When `focusId` is set, it zooms to just that hospital's scans instead — this is
 * what a legend row click drives, so a supervisor can find one site on the map.
 */
function FitBounds({ points, focusId }: { points: ScanMapPoint[]; focusId: string | null }) {
  const map = useMap();
  const subset = focusId ? points.filter((p) => (p.hospital_id || "unknown") === focusId) : points;
  const signature = (focusId || "") + "|" + subset.map((p) => p.id).join(",");
  useEffect(() => {
    if (subset.length === 0) return;
    const bounds = L.latLngBounds(subset.map((p) => [p.gps_lat, p.gps_lng] as [number, number]));
    // Zoom in tighter when honing in on a single site; fit-all stays wider.
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: focusId ? 16 : 17 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
  return null;
}

/** Renders a density heatmap layer using leaflet.heat. */
function HeatLayer({ points, visible }: { points: ScanMapPoint[]; visible: boolean }) {
  const map = useMap();
  const layerRef = useRef<any>(null);

  useEffect(() => {
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
    if (!visible || points.length === 0) return;

    const heatPoints = points.map((p) => [p.gps_lat, p.gps_lng, 0.6] as [number, number, number]);
    const layer = (L as any).heatLayer(heatPoints, {
      radius: 22,
      blur: 18,
      maxZoom: 17,
      // On-brand gradient: brand blue → teal → orange → red.
      gradient: { 0.2: "#3B7BBE", 0.45: "#046C98", 0.7: "#F5B11A", 1.0: "#EE3E2E" },
    });
    layer.addTo(map);
    layerRef.current = layer;

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, points, visible]);

  return null;
}

/**
 * Props let a parent (e.g. LiveActivityPage) lift the time-range filter up so it
 * can drive both the map AND a sibling activity feed. When a `range` prop is
 * provided the control becomes fully controlled; otherwise the component manages
 * its own range state (standalone `/scan-map` route).
 */
interface ScanMapPageProps {
  range?: TimeRangeKey;
  onRangeChange?: (range: TimeRangeKey) => void;
  customStart?: string;
  onCustomStartChange?: (value: string) => void;
  customEnd?: string;
  onCustomEndChange?: (value: string) => void;
}

export function ScanMapPage(props: ScanMapPageProps = {}) {
  const { t, lang } = useTranslation();
  // Localized labels for each time range, keyed by range key.
  const timeRangeLabels: Record<TimeRangeKey, string> = {
    "1h": t("map.rangeLastHour", "Last hour"),
    "24h": t("map.rangeLast24h", "Last 24h"),
    "7d": t("map.rangeLast7d", "Last 7 days"),
    "30d": t("map.rangeLast30d", "Last 30 days"),
    custom: t("map.rangeCustom", "Custom range"),
  };
  const [scans, setScans] = useState<ScanMapPoint[]>([]);
  // Total matching scans server-side (may exceed `scans.length` when capped).
  const [total, setTotal] = useState(0);
  const [limited, setLimited] = useState(false);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Time-range filter: controlled by the parent when props are supplied, else
  // self-managed. The setters fall back to internal state in standalone mode.
  const [internalRange, setInternalRange] = useState<TimeRangeKey>("24h");
  const [internalCustomStart, setInternalCustomStart] = useState("");
  const [internalCustomEnd, setInternalCustomEnd] = useState("");
  const range = props.range ?? internalRange;
  const setRange = props.onRangeChange ?? setInternalRange;
  const customStart = props.customStart ?? internalCustomStart;
  const setCustomStart = props.onCustomStartChange ?? setInternalCustomStart;
  const customEnd = props.customEnd ?? internalCustomEnd;
  const setCustomEnd = props.onCustomEndChange ?? setInternalCustomEnd;

  // Shared, persisted site selection (follows the user across pages). A local
  // filter backs the single-officer view — see below.
  const [stickyHospitalId, setStickyHospital] = useStickyHospital();
  const [localHospitalId, setLocalHospital] = useState<string>("");

  // ── Officer mode ──────────────────────────────────────────────────────────
  // When an officer is selected (via the search box or a deep link from the
  // Officers page: /supervisor/scan-map?officerId=…&officerName=…), the map
  // shows only that officer's attributed scans, color-coded by time — oldest
  // blue, newest red — instead of by hospital.
  const [searchParams, setSearchParams] = useSearchParams();
  const [officerRoster, setOfficerRoster] = useState<Officer[]>([]);
  const [officerQuery, setOfficerQuery] = useState("");
  const officerId = searchParams.get("officerId") || "";
  const officerName = searchParams.get("officerName") || "";

  // Single-officer view uses a LOCAL hospital filter so the shared/sticky site
  // selection can never hide that officer's scans at another site. The general
  // map (and the copy embedded in Live Activity) follows the shared selection.
  const isOfficerView = !!officerId;
  const hospitalId = isOfficerView ? localHospitalId : (stickyHospitalId ?? "");
  const setHospitalId = (v: string) =>
    isOfficerView ? setLocalHospital(v) : setStickyHospital(v || undefined);

  useEffect(() => {
    fetchOfficers({ status: "ALL" })
      .then(setOfficerRoster)
      .catch(() => setOfficerRoster([]));
  }, []);

  const selectOfficer = (o: Officer | null) => {
    setOfficerQuery("");
    if (o) setSearchParams({ officerId: o.id, officerName: o.name }, { replace: true });
    else setSearchParams({}, { replace: true });
  };

  const officerMatches = useMemo(() => {
    const q = officerQuery.trim().toLowerCase();
    if (!q) return [];
    return officerRoster
      .filter((o) => o.name.toLowerCase().includes(q) || o.n_number.toLowerCase().includes(q))
      .slice(0, 8);
  }, [officerQuery, officerRoster]);
  // Map view settings persist across visits/reloads (see useStickyState).
  const [autoRefresh, setAutoRefresh] = useStickyState("scanmap.autoRefresh", false, boolTo, boolFrom);
  const [showHeatmap, setShowHeatmap] = useStickyState("scanmap.heatmap", false, boolTo, boolFrom);
  // "Misplaced" view: instead of the on-site scans, show only the outliers whose
  // GPS landed >1km from their checkpoint (or out of region). Off by default.
  const [showMisplaced, setShowMisplaced] = useStickyState("scanmap.showMisplaced", false, boolTo, boolFrom);
  // Count of misplaced outliers in scope, surfaced on the toggle even while the
  // on-site view is active so the user knows there are anomalies to review.
  const [misplacedCount, setMisplacedCount] = useState(0);
  // "Anomalies" view: scans that form an impossible-speed hop with an adjacent
  // scan by the same officer (one officer credited across distant sites in
  // seconds). Off by default; mutually exclusive with the Misplaced view.
  const [showAnomalies, setShowAnomalies] = useStickyState("scanmap.showAnomalies", false, boolTo, boolFrom);
  const [anomalyCount, setAnomalyCount] = useState(0);
  // Pull indoor-GPS-drift scans toward their building center for display. On by default.
  const [snapToBuilding, setSnapToBuilding] = useStickyState("scanmap.snapToBuilding", true, boolTo, boolFrom);
  // Light is the default theme, per the G-Data brand styling.
  const [theme, setTheme] = useStickyState<"dark" | "light">(
    "scanmap.theme",
    "light",
    (v) => v,
    (s) => (s === "dark" ? "dark" : "light")
  );
  // Map tile style index (CartoDB / Standard / Voyager / Positron / Satellite / Watercolor),
  // chosen via the Layers dropdown and persisted to localStorage. Independent of
  // the dark/light toggle.
  const [styleIndex, setStyleIndex] = useState<number>(() => {
    const saved = Number(localStorage.getItem(MAP_STYLE_STORAGE_KEY));
    // A previously chosen style wins; otherwise open on the Hybrid satellite view.
    return Number.isInteger(saved) && saved >= 0 && saved < MAP_STYLES.length ? saved : DEFAULT_STYLE_INDEX;
  });
  // Whether the Layers style-picker dropdown is open.
  const [styleMenuOpen, setStyleMenuOpen] = useState(false);
  const styleMenuRef = useRef<HTMLDivElement>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    localStorage.setItem(MAP_STYLE_STORAGE_KEY, String(styleIndex));
  }, [styleIndex]);

  // Close the style dropdown on outside click or Escape.
  useEffect(() => {
    if (!styleMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (styleMenuRef.current && !styleMenuRef.current.contains(e.target as Node)) {
        setStyleMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setStyleMenuOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [styleMenuOpen]);

  // Legend drag + minimize state. `legendPos` is null until the user first drags,
  // in which case the legend uses its default bottom-right placement.
  const mapWrapRef = useRef<HTMLDivElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ offsetX: number; offsetY: number } | null>(null);
  const [legendPos, setLegendPos] = useState<{ left: number; top: number } | null>(null);
  const [legendCollapsed, setLegendCollapsed] = useStickyState("scanmap.legendCollapsed", false, boolTo, boolFrom);
  // When set, the map zooms to just this hospital's scans (clicked from the
  // legend); null = fit all hospitals. Cleared by clicking the same row again.
  const [focusHospitalId, setFocusHospitalId] = useState<string | null>(null);
  // Brief "green glow" pulse on the total-scans badge whenever the live count
  // ticks up (e.g. an auto-refresh brings in new scans).
  const [scanBump, setScanBump] = useState(false);
  const prevTotalRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevTotalRef.current;
    prevTotalRef.current = total;
    if (prev != null && total > prev) {
      setScanBump(true);
      const id = setTimeout(() => setScanBump(false), 1400);
      return () => clearTimeout(id);
    }
  }, [total]);

  const onLegendDragStart = (e: React.MouseEvent) => {
    const wrap = mapWrapRef.current;
    const el = legendRef.current;
    if (!wrap || !el) return;
    const wrapRect = wrap.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    dragState.current = { offsetX: e.clientX - elRect.left, offsetY: e.clientY - elRect.top };
    // Anchor to the current on-screen position so the first drag doesn't jump.
    setLegendPos({ left: elRect.left - wrapRect.left, top: elRect.top - wrapRect.top });
    e.preventDefault();
  };

  // Window-level move/up listeners so dragging keeps working past the legend edges.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const ds = dragState.current;
      const wrap = mapWrapRef.current;
      const el = legendRef.current;
      if (!ds || !wrap || !el) return;
      const wrapRect = wrap.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      let left = e.clientX - wrapRect.left - ds.offsetX;
      let top = e.clientY - wrapRect.top - ds.offsetY;
      // Keep the legend within the map bounds.
      left = Math.max(0, Math.min(left, wrapRect.width - elRect.width));
      top = Math.max(0, Math.min(top, wrapRect.height - elRect.height));
      setLegendPos({ left, top });
    };
    const onUp = () => {
      dragState.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const isDark = theme === "dark";

  // Effective tile layer: the cycled style crossed with the dark/light variant.
  const activeStyle = MAP_STYLES[styleIndex] ?? MAP_STYLES[0];
  const activeTile = isDark ? activeStyle.dark : activeStyle.light;

  // Theme-aware chrome tokens. Light = warm tan/cream; dark = medium gray (#333), not black.
  const panel = {
    bg: isDark ? "#333333" : "#F5F0E6",
    bgStyle: isDark ? { backgroundColor: "#333333" } : { backgroundColor: "#EFDCB1" },
    border: isDark ? "#4A4A4A" : "#E1BA5C",
    text: isDark ? "#FFFFFF" : "#333333",
  };

  // Inactive control buttons (active state stays branded per-button).
  const inactiveBtn = isDark
    ? "bg-[#444444] text-gray-100 hover:bg-[#555555]"
    : "bg-[#E9CB84] text-[#333333] hover:bg-[#E0BD6F]";

  // Marker outline color: bold, high-contrast neutral for accessibility.
  const markerStroke = isDark ? "#FFFFFF" : "#333333";

  // SVG renderer bound to the glow pane, so the soft-glow dots can be blurred by
  // that pane's CSS filter (canvas markers can't be individually blurred).
  const glowRenderer = useMemo(() => L.svg({ pane: "scanGlow", padding: 1 }), []);

  // Load the hospital list once for the filter + legend labels.
  useEffect(() => {
    fetchHospitals()
      .then(setHospitals)
      .catch(() => setHospitals([]));
  }, []);

  const loadScans = useMemo(
    () => async (silent = false) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const params: Record<string, string | undefined> = {};
        if (hospitalId) params.hospitalId = hospitalId;
        if (officerId) params.officerId = officerId;
        if (range === "custom") {
          if (customStart) params.startDate = new Date(customStart).toISOString();
          if (customEnd) params.endDate = new Date(customEnd).toISOString();
        } else {
          const start = startDateForRange(range);
          if (start) params.startDate = start.toISOString();
        }
        // Anomalies and Misplaced are mutually-exclusive alternate views.
        if (showAnomalies) params.view = "anomalous";
        else if (showMisplaced) params.view = "misplaced";
        const data = await fetchScanMap({ ...params, limit: 5000 });
        setScans(data.scans);
        setTotal(data.total);
        setLimited(data.limited);
        setMisplacedCount(data.misplaced);
        setAnomalyCount(data.anomalous);
        setLastUpdated(new Date());
      } catch (e: any) {
        setError(e?.message || t("map.loadFailed", "Failed to load scans"));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [hospitalId, officerId, range, customStart, customEnd, showMisplaced, showAnomalies]
  );

  useEffect(() => {
    void loadScans();
  }, [loadScans]);

  // Auto-refresh polling.
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => void loadScans(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [autoRefresh, loadScans]);

  // Canonical hospital→color map, built from the FULL hospital list (not just the
  // hospitals in the current scans) so colors stay stable across time ranges and
  // match every other view. Shared with the comparison chart and calendar.
  const hospitalColors = useMemo(() => buildHospitalColorMap(hospitals), [hospitals]);

  // Officer mode: min/max scan time across loaded points, for the gradient.
  const timeSpan = useMemo(() => {
    if (scans.length === 0) return { min: 0, max: 1 };
    let min = Infinity;
    let max = -Infinity;
    for (const s of scans) {
      const ts = Date.parse(s.scanned_at);
      if (ts < min) min = ts;
      if (ts > max) max = ts;
    }
    return { min, max: Math.max(max, min + 1) };
  }, [scans]);


  // Legend entries: hospital name -> color + scan count, for hospitals present in
  // the data. Counts reflect the currently loaded (on-screen) scans so they stay
  // accurate to the active time range / hospital filter; sorted most scans first.
  const legend = useMemo(() => {
    const nameById = new Map(hospitals.map((h) => [h.id, h.name]));
    const seen = new Map<string, { id: string; name: string; color: string; count: number }>();
    for (const s of scans) {
      const id = s.hospital_id || "unknown";
      const entry = seen.get(id);
      if (entry) {
        entry.count += 1;
      } else {
        seen.set(id, {
          id,
          name: nameById.get(id || "") || s.hospital_name || t("map.unknownHospital", "Unknown"),
          color: hospitalColors.colorFor({ id: s.hospital_id, name: nameById.get(id || "") || s.hospital_name }),
          count: 1,
        });
      }
    }
    return Array.from(seen.values()).sort((a, b) => b.count - a.count);
  }, [scans, hospitals, hospitalColors, t]);

  // Display coordinates: optionally snapped toward each scan's building center to
  // counter indoor GPS drift. Falls back to raw coords when snapping is off or no
  // building center is known. The DB data is never mutated — this is display-only.
  const displayScans = useMemo(() => {
    if (!snapToBuilding) return scans;
    return scans.map((s) => {
      const [lat, lng] = snapToBuildingCenter(s.gps_lat, s.gps_lng, s.hospital_id);
      return lat === s.gps_lat && lng === s.gps_lng ? s : { ...s, gps_lat: lat, gps_lng: lng };
    });
  }, [scans, snapToBuilding]);

  // The fill color a scan's dot uses — shared by the marker and its glow halo so
  // the two always match. (Anomaly purple / misplaced red / officer time-gradient
  // / else the hospital's canonical color.)
  const scanColor = (s: ScanMapPoint) =>
    showAnomalies
      ? "#7C3A73"
      : showMisplaced
      ? "#EE3E2E"
      : officerId
      ? timeGradientColor(Date.parse(s.scanned_at), timeSpan.min, timeSpan.max)
      : hospitalColors.colorFor({ id: s.hospital_id, name: s.hospital_name });

  // Deduped (position, color) points for the soft same-hue glow drawn under each
  // dot. Collapsing identical stacked scans keeps their translucent halos from
  // compounding into a solid blob and keeps the extra canvas layer cheap.
  const glowPoints = useMemo(() => {
    const m = new Map<string, { lat: number; lng: number; color: string }>();
    for (const s of displayScans) {
      if (s.gps_lat == null || s.gps_lng == null) continue;
      const color = scanColor(s);
      const key = `${s.gps_lat.toFixed(5)},${s.gps_lng.toFixed(5)},${color}`;
      if (!m.has(key)) m.set(key, { lat: s.gps_lat, lng: s.gps_lng, color });
    }
    return [...m.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayScans, showAnomalies, showMisplaced, officerId, timeSpan, hospitalColors]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <MapPin className="h-6 w-6 text-[#3B7BBE]" />
            {t("map.pageTitle", "GuardAI Scan Map")}
          </h1>
          <p className="text-sm font-medium text-gray-600">
            {t("map.pageSubtitle", "Live geographic view of where scan events occurred.")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="rounded-lg px-5 py-2.5 text-lg font-extrabold tracking-tight transition-all duration-500"
            style={{
              background: "linear-gradient(180deg, #E8BE4A 0%, #D9A828 100%)",
              color: "#3A2D06",
              // Green glow + slight pop when the count rises, easing back over 500ms.
              boxShadow: scanBump
                ? "0 0 0 3px rgba(74, 222, 128, 0.55), 0 0 24px 6px rgba(34, 197, 94, 0.6)"
                : "0 1px 2px rgba(0, 0, 0, 0.18)",
              transform: scanBump ? "scale(1.05)" : "scale(1)",
            }}
          >
            {limited
              ? t("map.showingMaxScans", "Showing {shown} (max) of {total} scans")
                  .replace("{shown}", scans.length.toLocaleString(lang))
                  .replace("{total}", total.toLocaleString(lang))
              : t("map.totalScans", "{total} scans").replace("{total}", total.toLocaleString(lang))}
          </span>
          {limited && (
            <span className="text-xs font-semibold text-[#EA6D24]">
              {t(
                "map.limitedWarning",
                "Map displays up to {max} scans max. Please narrow your time range or hospital filter to see specific data."
              ).replace("{max}", scans.length.toLocaleString(lang))}
            </span>
          )}
          {lastUpdated && (
            <span className="text-xs font-semibold text-gray-500">
              {t("map.updatedAt", "Updated {time}").replace(
                "{time}",
                new Intl.DateTimeFormat(lang, {
                  hour: "numeric",
                  minute: "2-digit",
                  second: "2-digit",
                }).format(lastUpdated)
              )}
            </span>
          )}
        </div>
      </div>

      {/* Controls */}
      <div
        className="flex flex-wrap items-center gap-3 rounded-xl border p-3 shadow-sm"
        style={{ ...panel.bgStyle, borderColor: panel.border, color: panel.text }}
      >
        {/* Time range */}
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" style={{ color: panel.text }} />
          <div className="flex flex-wrap gap-1">
            {TIME_RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                className={cn(
                  "rounded-lg px-3 py-1.5 text-xs font-bold transition-colors",
                  range === r.key
                    ? "bg-[#3B7BBE] text-white shadow-sm"
                    : inactiveBtn
                )}
              >
                {timeRangeLabels[r.key]}
              </button>
            ))}
          </div>
        </div>

        {range === "custom" && (
          <div className="flex items-center gap-2">
            <input
              type="datetime-local"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="rounded-lg border px-2 py-1.5 text-xs font-semibold"
              style={{ borderColor: panel.border, backgroundColor: isDark ? "#444444" : "#FFFFFF", color: panel.text }}
            />
            <span className="text-xs font-semibold" style={{ color: panel.text }}>{t("map.to", "to")}</span>
            <input
              type="datetime-local"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="rounded-lg border px-2 py-1.5 text-xs font-semibold"
              style={{ borderColor: panel.border, backgroundColor: isDark ? "#444444" : "#FFFFFF", color: panel.text }}
            />
          </div>
        )}

        {/* Hospital filter */}
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4" style={{ color: panel.text }} />
          <select
            value={hospitalId}
            onChange={(e) => setHospitalId(e.target.value)}
            className="rounded-lg border px-2 py-1.5 text-xs font-bold"
            style={{ borderColor: panel.border, backgroundColor: isDark ? "#444444" : "#FFFFFF", color: panel.text }}
          >
            <option value="">{t("map.allHospitals", "All hospitals")}</option>
            {hospitals.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        </div>

        {/* Officer filter — search by name or N number; active filter shows as a chip */}
        <div className="relative flex items-center gap-2">
          <UserCheck className="h-4 w-4" style={{ color: panel.text }} />
          {officerId ? (
            <span
              className="flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs font-bold"
              style={{ borderColor: panel.border, backgroundColor: isDark ? "#444444" : "#FFFFFF", color: panel.text }}
            >
              {officerName || t("map.officer", "Officer")}
              <button
                onClick={() => selectOfficer(null)}
                aria-label={t("map.clearOfficer", "Clear officer filter")}
                className="opacity-70 hover:opacity-100"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ) : (
            <>
              <input
                value={officerQuery}
                onChange={(e) => setOfficerQuery(e.target.value)}
                placeholder={t("map.searchOfficer", "Search officer…")}
                className="w-36 rounded-lg border px-2 py-1.5 text-xs font-bold"
                style={{ borderColor: panel.border, backgroundColor: isDark ? "#444444" : "#FFFFFF", color: panel.text }}
              />
              {officerMatches.length > 0 && (
                <div
                  className="absolute left-6 top-9 z-[1100] w-56 overflow-hidden rounded-lg border shadow-lg"
                  style={{ borderColor: panel.border, backgroundColor: isDark ? "#333333" : "#FFFFFF" }}
                >
                  {officerMatches.map((o) => (
                    <button
                      key={o.id}
                      onClick={() => selectOfficer(o)}
                      className="block w-full px-3 py-2 text-left text-xs font-semibold hover:bg-blue-50"
                      style={{ color: panel.text }}
                    >
                      {o.name} <span className="opacity-60">({o.n_number})</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* GPS accuracy disclaimer */}
        <div className="group relative flex items-center">
          <Info className="h-4 w-4 cursor-help" style={{ color: panel.text, opacity: 0.7 }} />
          <span
            className="pointer-events-none absolute left-0 top-6 z-[1100] hidden w-64 rounded-lg border px-3 py-2 text-xs font-semibold shadow-lg group-hover:block"
            style={{
              borderColor: panel.border,
              backgroundColor: isDark ? "#333333" : "#FFFFFF",
              color: panel.text,
            }}
          >
            {t(
              "map.gpsDisclaimer",
              "Scan positions reflect device GPS accuracy. Indoor signals may vary by 10–50m from actual location."
            )}
          </span>
        </div>

        {/* min-w-0 + flex-wrap: this group must wrap INSIDE the toolbar card
            instead of overflowing its right edge at narrow widths */}
        <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
          {/* Misplaced-scans view toggle. The badge shows how many outliers are
              in scope so the anomaly is visible even from the on-site view. */}
          <button
            onClick={() => setShowMisplaced((v) => { const nv = !v; if (nv) setShowAnomalies(false); return nv; })}
            className={cn(
              "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors",
              showMisplaced ? "bg-[#EE3E2E] text-white shadow-sm" : inactiveBtn
            )}
            title={t("map.misplacedTitle", "Show only scans whose GPS landed more than 1km from their checkpoint")}
          >
            <AlertTriangle className="h-4 w-4" />
            {showMisplaced ? t("map.misplacedOn", "Misplaced") : t("map.misplacedOff", "Misplaced")}
            {misplacedCount > 0 && (
              <span
                className={cn(
                  "ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none",
                  showMisplaced ? "bg-white/25 text-white" : "bg-[#EE3E2E] text-white"
                )}
              >
                {misplacedCount}
              </span>
            )}
          </button>

          {/* Anomalous-movement view toggle. Flags scans where one officer is
              credited across distant sites faster than anyone could travel —
              an attribution artifact, not a real patrol. */}
          <button
            onClick={() => setShowAnomalies((v) => { const nv = !v; if (nv) setShowMisplaced(false); return nv; })}
            className={cn(
              "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors",
              showAnomalies ? "bg-[#7C3A73] text-white shadow-sm" : inactiveBtn
            )}
            title={t("map.anomaliesTitle", "Show scans where one officer is credited across distant sites faster than physically possible")}
          >
            <Zap className="h-4 w-4" />
            {t("map.anomalies", "Anomalies")}
            {anomalyCount > 0 && (
              <span
                className={cn(
                  "ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none",
                  showAnomalies ? "bg-white/25 text-white" : "bg-[#7C3A73] text-white"
                )}
              >
                {anomalyCount}
              </span>
            )}
          </button>

          {/* Snap-to-building toggle */}
          <button
            onClick={() => setSnapToBuilding((v) => !v)}
            className={cn(
              "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors",
              snapToBuilding
                ? "bg-[#046C98] text-white shadow-sm"
                : inactiveBtn
            )}
            title={t("map.snapToBuildingTitle", "Snap GPS dots toward building centers to counter indoor GPS drift")}
          >
            <Crosshair className="h-4 w-4" />
            {t("map.snapToBuilding", "Snap to building")}
          </button>

          {/* Heatmap toggle */}
          <button
            onClick={() => setShowHeatmap((v) => !v)}
            className={cn(
              "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors",
              showHeatmap
                ? "bg-[#EA6D24] text-white shadow-sm"
                : inactiveBtn
            )}
            title={t("map.heatmapTitle", "Toggle density heatmap")}
          >
            <Flame className="h-4 w-4" />
            {showHeatmap ? t("map.heatmap", "Heatmap") : t("map.markers", "Markers")}
          </button>

          {/* Map style picker — dropdown listing all tile providers, independent of theme */}
          <div className="relative" ref={styleMenuRef}>
            <button
              onClick={() => setStyleMenuOpen((v) => !v)}
              className={cn(
                "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors",
                inactiveBtn
              )}
              title={t("map.mapStyle", "Map style: {name}").replace("{name}", activeStyle.name)}
              aria-haspopup="listbox"
              aria-expanded={styleMenuOpen}
            >
              <Layers className="h-4 w-4" />
              {activeStyle.name}
              <ChevronDown
                className={cn("h-3.5 w-3.5 transition-transform", styleMenuOpen && "rotate-180")}
              />
            </button>
            {styleMenuOpen && (
              <div
                role="listbox"
                className="absolute right-0 z-[1200] mt-1 w-40 overflow-hidden rounded-lg border shadow-lg"
                style={{
                  borderColor: panel.border,
                  backgroundColor: isDark ? "#333333" : "#FFFFFF",
                }}
              >
                {MAP_STYLES.map((s, i) => {
                  const selected = i === styleIndex;
                  return (
                    <button
                      key={s.name}
                      role="option"
                      aria-selected={selected}
                      onClick={() => {
                        setStyleIndex(i);
                        setStyleMenuOpen(false);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-bold transition-colors"
                      style={{
                        color: selected ? "#FFFFFF" : panel.text,
                        backgroundColor: selected ? "#3B7BBE" : "transparent",
                      }}
                      onMouseEnter={(e) => {
                        if (!selected)
                          e.currentTarget.style.backgroundColor = isDark ? "#444444" : "#F5F0E6";
                      }}
                      onMouseLeave={(e) => {
                        if (!selected) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <Check
                        className={cn("h-3.5 w-3.5 flex-shrink-0", !selected && "invisible")}
                        style={{ color: selected ? "#DDAF41" : undefined }}
                      />
                      {s.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Dark/light toggle — Sun/Moon, swaps the light & dark tile variants */}
          <button
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
            className={cn(
              "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors",
              inactiveBtn
            )}
            title={isDark ? t("map.switchToLight", "Switch to light map") : t("map.switchToDark", "Switch to dark map")}
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {isDark ? t("map.light", "Light") : t("map.dark", "Dark")}
          </button>

          {/* Auto-refresh toggle */}
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            className={cn(
              "flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors",
              autoRefresh
                ? "bg-[#A1BF8C] text-[#333333] shadow-sm"
                : inactiveBtn
            )}
            title={t("map.autoRefreshTitle", "Auto-refresh every 30s")}
          >
            <RefreshCw className={cn("h-4 w-4", autoRefresh && "animate-spin")} />
            {t("map.autoRefresh", "Auto-refresh")}
          </button>

          {/* Manual refresh */}
          <button
            onClick={() => void loadScans()}
            className={cn(
              "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors",
              inactiveBtn
            )}
            style={{ borderColor: panel.border }}
          >
            <RefreshCw className="h-4 w-4" />
            {t("common.refresh", "Refresh")}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700">
          {error}
        </div>
      )}

      {/* Map */}
      <div
        ref={mapWrapRef}
        className="relative overflow-hidden rounded-xl border shadow-sm"
        style={{ borderColor: panel.border }}
      >
        <MapContainer attributionControl={false}
          center={ATLANTA_CENTER}
          zoom={11}
          style={{ height: "calc(100vh - 280px)", minHeight: "480px", width: "100%" }}
          preferCanvas
        >
          <TileLayer key={`${styleIndex}-${theme}`} url={activeTile.url} attribution={activeTile.attribution} />
          {/* Hybrid: transparent road + label reference tiles drawn over the imagery. */}
          {activeStyle.overlays?.map((overlayUrl, i) => (
            <TileLayer key={`overlay-${styleIndex}-${i}`} url={overlayUrl} attribution="" />
          ))}

          <FitBounds points={displayScans} focusId={focusHospitalId} />
          <HeatLayer points={displayScans} visible={showHeatmap} />

          {/* Soft same-hue glow: one filled dot per location, rendered as SVG in a
              dedicated low-z pane whose CSS blur turns it into a gentle aura — no
              hard ring even when many scans stack. Faint and non-interactive. */}
          {!showHeatmap && (
            <Pane name="scanGlow" style={{ zIndex: 380, filter: "blur(7px)", opacity: 0.35 }}>
              {glowPoints.map((g, i) => (
                <CircleMarker
                  key={`glow-${i}`}
                  center={[g.lat, g.lng]}
                  radius={8}
                  renderer={glowRenderer}
                  pathOptions={{ stroke: false, fillColor: g.color, fillOpacity: 0.85, interactive: false }}
                />
              ))}
            </Pane>
          )}

          {!showHeatmap &&
            displayScans.map((s) => {
              // Officer mode: color by WHEN the scan happened — oldest in the
              // window blue, newest red. Otherwise, color by hospital as before.
              // Misplaced view: uniform warning red, with a dashed line back to
              // where the scan should have been (its checkpoint anchor).
              const isAnom = !!s.anomalous;
              const color = scanColor(s);
              // Anomalous scans get a bold purple ring so they stand out even in
              // the normal on-site view, not only under the Anomalies toggle.
              const stroke = showAnomalies
                ? "#3B1338"
                : showMisplaced
                ? "#7F1D1D"
                : isAnom
                ? "#7C3A73"
                : markerStroke;
              const hasExpected = showMisplaced && s.expected_lat != null && s.expected_lng != null;
              return (
                <Fragment key={s.id}>
                  {hasExpected && (
                    <Polyline
                      positions={[
                        [s.gps_lat, s.gps_lng],
                        [s.expected_lat as number, s.expected_lng as number],
                      ]}
                      pathOptions={{ color: "#EE3E2E", weight: 2, dashArray: "6 6", opacity: 0.75 }}
                    />
                  )}
                  <CircleMarker
                    center={[s.gps_lat, s.gps_lng]}
                    radius={showMisplaced || showAnomalies || isAnom ? 9 : 8}
                    pathOptions={{
                      color: stroke,
                      fillColor: color,
                      fillOpacity: 0.9,
                      weight: isAnom || showAnomalies || showMisplaced ? 3 : 2.5,
                    }}
                  >
                  <Popup className={isDark ? "gdata-popup-dark" : "gdata-popup-light"}>
                    <div className="space-y-1 text-xs">
                      <div
                        className="text-sm font-bold"
                        style={{ color: isDark ? "#FFFFFF" : "#333333" }}
                      >
                        {s.location_name}
                      </div>
                      <div className="font-medium" style={{ color: isDark ? "#E4E4E3" : "#444444" }}>
                        <span className="font-bold">{t("map.popupHospital", "Hospital:")}</span> {s.hospital_name}
                      </div>
                      <div className="font-medium" style={{ color: isDark ? "#E4E4E3" : "#444444" }}>
                        <span className="font-bold">{t("map.popupTime", "Time:")}</span>{" "}
                        {new Intl.DateTimeFormat(lang, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          second: "2-digit",
                        }).format(new Date(s.scanned_at))}
                      </div>
                      <div className="font-medium" style={{ color: isDark ? "#E4E4E3" : "#444444" }}>
                        <span className="font-bold">{t("map.popupScanner", "Scanner:")}</span>{" "}
                        {s.scanner_id ? s.scanner_id.slice(0, 8) : "—"}
                      </div>
                      {s.officer_name && (
                        <div className="font-medium" style={{ color: isDark ? "#E4E4E3" : "#444444" }}>
                          <span className="font-bold">{t("map.popupOfficer", "Officer:")}</span> {s.officer_name}
                        </div>
                      )}
                      {isAnom && (
                        <div className="font-bold" style={{ color: "#7C3A73" }}>
                          ⚠ {s.anomaly_reason === "hospital"
                            ? t("map.anomalousHospital", "Anomalous — plotted far from its labeled hospital")
                            : s.anomaly_reason === "both"
                            ? t("map.anomalousBoth", "Anomalous — impossible travel and far from its hospital")
                            : t("map.anomalousMovement", "Anomalous — impossible travel between this officer's scans")}
                        </div>
                      )}
                      {showMisplaced && s.distance_m != null && (
                        <div className="font-bold" style={{ color: "#EE3E2E" }}>
                          {t("map.metersFromCheckpoint", "{n} m from its checkpoint").replace("{n}", s.distance_m.toLocaleString(lang))}
                        </div>
                      )}
                    </div>
                  </Popup>
                  </CircleMarker>
                </Fragment>
              );
            })}
        </MapContainer>

        {/* Officer-mode gradient legend */}
        {officerId && scans.length > 0 && (
          <div
            className="absolute bottom-4 left-4 z-[1000] w-72 rounded-xl border p-3 shadow-lg"
            style={{ borderColor: panel.border, backgroundColor: isDark ? "rgba(51,51,51,.95)" : "rgba(255,255,255,.95)" }}
          >
            <div className="mb-1 text-xs font-bold" style={{ color: panel.text }}>
              {officerName} · {scans.length} {t("map.scansLabel", "scans")}
            </div>
            <div
              className="h-2 w-full rounded-sm"
              style={{ background: "linear-gradient(to right, hsl(217,82%,52%), hsl(150,82%,48%), hsl(45,90%,52%), hsl(0,82%,52%))" }}
            />
            <div className="mt-1 flex items-center justify-between text-[10px] font-semibold" style={{ color: panel.text, opacity: 0.8 }}>
              <span>{t("map.earliest", "Earliest")}: {new Intl.DateTimeFormat(lang, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(timeSpan.min))}</span>
              <span>{t("map.latest", "Latest")}: {new Intl.DateTimeFormat(lang, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(timeSpan.max))}</span>
            </div>
            <div className="mt-1.5 text-[10px] leading-snug" style={{ color: panel.text, opacity: 0.65 }}>
              {t("map.trailNote", "Dots are colored by scan time. No path between scans is drawn — only recorded scan locations are shown.")}
            </div>
          </div>
        )}

        {/* Legend — draggable + minimizable */}
        {!officerId && legend.length > 0 && (
          <div
            ref={legendRef}
            className={cn(
              "absolute z-[1000] rounded-lg border text-base shadow-lg backdrop-blur",
              legendPos ? "" : "bottom-4 right-4"
            )}
            style={{
              ...panel.bgStyle,
              borderColor: panel.border,
              color: panel.text,
              backgroundColor: isDark ? "rgba(51,51,51,0.95)" : "rgba(239,220,177,0.95)",
              ...(legendPos ? { left: legendPos.left, top: legendPos.top } : {}),
            }}
          >
            {/* Drag handle / header bar */}
            <div
              onMouseDown={onLegendDragStart}
              className="flex cursor-move select-none items-center gap-2 rounded-t-lg px-3 py-2"
              style={{ borderBottom: legendCollapsed ? "none" : `1px solid ${panel.border}` }}
            >
              <GripVertical className="h-4 w-4 flex-shrink-0 opacity-60" />
              <span
                className="text-sm font-bold uppercase tracking-wide"
                style={{ color: isDark ? "#DDAF41" : "#7C3A73" }}
              >
                {legendCollapsed ? t("map.legend", "Legend") : t("domain.hospitals", "Hospitals")}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLegendCollapsed((v) => !v);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className="ml-auto rounded p-0.5 hover:opacity-70"
                style={{ color: panel.text }}
                title={legendCollapsed ? t("map.expandLegend", "Expand legend") : t("map.minimizeLegend", "Minimize legend")}
              >
                {legendCollapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            </div>

            {!legendCollapsed && (
              <div className="max-h-64 space-y-1.5 overflow-y-auto px-3 pb-3 pt-2">
                {legend.map((l) => {
                  const active = focusHospitalId === l.id;
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => setFocusHospitalId(active ? null : l.id)}
                      title={
                        active
                          ? t("map.showAllHospitals", "Show all hospitals")
                          : t("map.zoomToHospital", "Zoom the map to this location")
                      }
                      className="flex w-full cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                      style={{ backgroundColor: active ? `${l.color}33` : "transparent" }}
                    >
                      <span className="font-bold tabular-nums" style={{ color: panel.text }}>
                        {l.count.toLocaleString()}
                      </span>
                      <span
                        className="inline-block h-4 w-4 flex-shrink-0 rounded-full"
                        style={{
                          backgroundColor: l.color,
                          border: `2px solid ${markerStroke}`,
                          boxShadow: active ? `0 0 0 2px ${l.color}` : "none",
                        }}
                      />
                      <span className="font-semibold" style={{ color: panel.text }}>
                        {legendLabel(l.name)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-white/40 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#3B7BBE] border-t-transparent" />
              <span className="text-sm font-bold text-gray-700">{t("map.loadingScans", "Loading scans…")}</span>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && scans.length === 0 && !error && (
          <div className="absolute inset-0 z-[900] flex items-center justify-center bg-white/20">
            <div className="rounded-lg bg-white/90 px-4 py-3 text-sm font-semibold text-gray-700 shadow">
              {t("map.noGpsScans", "No GPS-tagged scans found for this time range.")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
